import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { useTourStore } from '@/stores/tourStore'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import {
  defaultTourGeometry,
  followStep,
  WAIT_AFTER_ROUTE_MS,
  WAIT_MS,
  TOUR_LAYER_ATTR,
  type StepPlace,
  type TourGeometry,
} from '@/services/tour/follow'
import { onRoute, placeRing, playedStep } from '@/services/tour/tourModel'
import { useRecordNoun } from '@/hooks/useRecordNoun'
import { TourScrim } from './TourScrim'
import { TourRing } from './TourRing'
import { CoachCard } from './CoachCard'
import { TourPanelChip } from './TourPanelChip'
import { MISSING_DEFAULT, stepAnnouncement } from './tourText'

interface TourHostProps {
  /** The layout seam, for tests (happy-dom has no layout). */
  geometry?: TourGeometry
}

function viewportOf(geo: TourGeometry) {
  return geo.viewport()
}

/**
 * Plays the page tour in progress (SA-1315, spec §1 to §3, §6): the scrim, the ring and the
 * coach card in one portal at the panel's level, under toasts. Renders nothing while idle.
 */
export function TourHost({ geometry }: TourHostProps) {
  const running = useTourStore((s) => s.tour !== null)
  return running ? <RunningTour geometry={geometry} /> : null
}

function RunningTour({ geometry }: TourHostProps) {
  const { tour, step, status, origin, next, back, exit } = useTourStore()
  const { pathname } = useLocation()
  const reducedMotion = useReducedMotion()
  const geo = useRef<TourGeometry | null>(null)
  if (!geo.current) geo.current = geometry ?? defaultTourGeometry(document, window)
  const [place, setPlace] = useState<StepPlace | null>(null)
  const [viewport, setViewport] = useState(() => viewportOf(geo.current!))

  // Focus returns to what opened the tour when it ends.
  const opener = useRef<HTMLElement | null>(null)
  useLayoutEffect(() => {
    const active = document.activeElement
    opener.current = active instanceof HTMLElement && active !== document.body ? active : null
    return () => {
      const to = opener.current
      opener.current = null
      if (to?.isConnected) to.focus({ preventScroll: true })
    }
  }, [])

  // A step after a route change waits longer: the page is still arriving.
  const lastPath = useRef(pathname)
  const lastStep = useRef(step)
  const shownEl = useRef<Element | null>(null)
  const plural = useRecordNoun().plural
  const target = tour ? playedStep(tour, step, plural) : null
  const targetKey = target ? `${target.mode}|${target.selector ?? ''}|${target.reveal ?? ''}` : ''
  useEffect(() => {
    if (!tour || !target) return
    const routeMoved = lastPath.current !== pathname
    const backward = lastStep.current > step
    lastPath.current = pathname
    lastStep.current = step
    setPlace(null)
    shownEl.current = null
    const store = useTourStore.getState

    // A go step completes on reaching its page, and is skipped when the person is already
    // there, except on the way back, where it stays until Next or the next move.
    if (target.mode === 'go' && onRoute(target.routes ?? [], pathname) && (!backward || routeMoved)) {
      store().next()
      return
    }
    if (target.mode === 'read' || !target.selector) {
      setViewport(viewportOf(geo.current!))
      store().setStatus(target.mode === 'read' ? 'shown' : 'missing')
      return
    }
    store().setStatus('finding')
    const handle = followStep({
      doc: document,
      geo: geo.current!,
      selector: target.selector,
      reveal: target.reveal,
      waitMs: routeMoved ? WAIT_AFTER_ROUTE_MS : WAIT_MS,
      onShown: (p, el) => {
        shownEl.current = el
        setViewport(viewportOf(geo.current!))
        setPlace(p)
        store().setStatus('shown')
      },
      onMissing: (p) => {
        shownEl.current = null
        setViewport(viewportOf(geo.current!))
        setPlace(p)
        store().setStatus('missing')
      },
    })
    // A do step completes on a click, or a change for a field, inside its control, after the
    // app has handled the event: the tour never clicks and never prevents (spec §8).
    let offDo = () => {}
    if (target.mode === 'do') {
      const thisTour = tour
      const thisStep = step
      let done = false
      const onEvent = (e: Event) => {
        const el = shownEl.current
        if (done || !el || !(e.target instanceof Node) || !el.contains(e.target)) return
        done = true
        window.setTimeout(() => {
          const now = store()
          if (now.tour === thisTour && now.step === thisStep) now.next()
        }, 0)
      }
      document.addEventListener('click', onEvent, true)
      document.addEventListener('change', onEvent, true)
      offDo = () => {
        document.removeEventListener('click', onEvent, true)
        document.removeEventListener('change', onEvent, true)
      }
    }
    return () => {
      handle.dispose()
      offDo()
    }
    // `target` is rebuilt every render; `targetKey` names what it is.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour, targetKey, step, pathname])

  if (!tour || !target) return null
  const missing = status === 'missing'
  const placed = status === 'shown' || missing
  const read = target.mode === 'read'
  const ring = place && !read ? placeRing(place.rect, viewport, place.headerInset) : null
  const announcement = !placed
    ? ''
    : stepAnnouncement(step, tour.steps.length, missing ? target.whenMissing || MISSING_DEFAULT : target.text)

  return createPortal(
    <div {...{ [TOUR_LAYER_ATTR]: '' }} className="pointer-events-none fixed inset-0 z-50 print:hidden" data-testid="tour-host">
      {!read && <TourScrim viewport={viewport} hole={ring} />}
      {status === 'shown' && ring && (
        <TourRing box={ring} stepKey={`${tour.id}:${step}`} reducedMotion={reducedMotion} />
      )}
      <p className="sr-only" role="status" aria-live="polite" data-testid="tour-live">
        {announcement}
      </p>
      {origin === 'panel' && (
        <TourPanelChip title={tour.title} step={step} total={tour.steps.length} onExit={exit} />
      )}
      {placed && (
        <CoachCard
          title={tour.title}
          text={target.text}
          step={step}
          total={tour.steps.length}
          anchor={ring}
          missing={missing}
          whenMissing={target.whenMissing}
          pressName={target.mode === 'do' ? target.name : undefined}
          expect={target.expect}
          corner={read}
          reducedMotion={reducedMotion}
          onNext={next}
          onBack={back}
          onExit={exit}
        />
      )}
    </div>,
    document.body
  )
}
